import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Avatar,
  Box,
  Paper,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import { useSnackbar } from "notistack";
import colors from "../../Constants/colors";
import { getWorkspace } from "../../Api/workspace";
import { Workspace, GetWorkspaceResponse } from "../../Models/workspace";
import { Edit } from "@mui/icons-material";
import UsersTable from "./WorkspaceUsersTable";
import WorkspaceQuestionListsTable from "./WorkspaceQuestionListTable";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { useTranslation } from "react-i18next";
import { terminologyToString } from "../../Helpers/stringHelpers";
import { useEvaluationModel } from "../../Query/useEvaluation";
import SharedEntitiesTable from "./SharedEntitiesTable";
import { useTerminology } from "../../Core/useTerminology";
import { usePredefinedSections } from "../../Query/usePredefinedSections";
import { AppendixStructure } from "../../Models/appendixStructure";
import PredefinedSectionPreviewModal from "./PredefinedSectionPreviewModal";
import UsageWidget from "../Usage/UsageWidget";

const SingleWorkspace: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation("workspaces");
  const terminology = useTerminology();
  const { enqueueSnackbar } = useSnackbar();
  const [workspaceData, setWorkspaceData] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { colors: workspace } = useWorkspaceColors();
  const { data: predefinedSectionsData } = usePredefinedSections(
    workspaceId ? parseInt(workspaceId, 10) : undefined,
  );
  const [previewStructure, setPreviewStructure] =
    useState<AppendixStructure | null>(null);
  const { data: modelData } = useEvaluationModel();
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (!workspaceId) {
      setError("Invalid workspace ID.");
      setLoading(false);
      return;
    }

    const fetchWorkspace = async () => {
      try {
        setLoading(true);
        const data: GetWorkspaceResponse = await getWorkspace(workspaceId);

        setWorkspaceData({
          ...data.workspace,
          permissions: data.permissions || [],
          workspace_certifications: data.workspace_certifications || [],
          workspace_question_lists: data.workspace_question_lists || [],
          experts: data.experts || [],
          companies: data.companies || [],
        });
      } catch {
        setError("Failed to fetch workspace details.");
      } finally {
        setLoading(false);
      }
    };

    fetchWorkspace();
  }, [workspaceId]);

  const parseNamespaces = (namespaces: any): string[] => {
    if (Array.isArray(namespaces)) {
      return namespaces;
    }
    if (typeof namespaces === "string") {
      try {
        return JSON.parse(namespaces.replace(/'/g, '"'));
      } catch {
        enqueueSnackbar("Failed to parse namespaces.", { variant: "error" });
      }
    }
    return [];
  };

  const handleEdit = () => {
    if (workspaceData) {
      const parsedNamespaces = parseNamespaces(workspaceData.namespaces);
      navigate("/workspace/create", {
        state: {
          workspace: {
            ...workspaceData,
            namespaces: parsedNamespaces,
          },
        },
      });
    }
  };

  if (loading) {
    return (
      <Loading
        backgroundColor={colors.gray100}
        color1={workspace.accent}
        color2={workspace.secondary}
      />
    );
  }

  if (error) {
    return (
      <Container>
        <Typography variant="h4" sx={{ color: colors.red }}>
          {error}
        </Typography>
      </Container>
    );
  }

  if (!workspaceData) {
    return (
      <Container>
        <Typography variant="h4" sx={{ color: colors.red }}>
          Workspace data not available.
        </Typography>
      </Container>
    );
  }

  const {
    name,
    description,
    icon_image,
    namespaces,
    user_company_limit,
    user_evaluation_limit,
    user_storage_limit,
    user_library_limit,
    workspace_admin,
    created_at,
    permissions = [],
    workspace_certifications = [],
    accessible_model_ids = [],
    experts = [],
  } = workspaceData;
  const formatDate = (dateString: string) => {
    if (!dateString) return t("notAvailable");
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? t("invalidDate") : date.toLocaleString();
  };

  const renderNamespaces = () => {
    if (!namespaces) {
      return t("noNamespaces");
    }

    if (Array.isArray(namespaces)) {
      return namespaces.length > 0 ? namespaces.join(", ") : t("noNamespaces");
    }

    if (typeof namespaces === "string") {
      try {
        const parsedNamespaces = JSON.parse(namespaces.replace(/'/g, '"'));
        if (Array.isArray(parsedNamespaces) && parsedNamespaces.length > 0) {
          return parsedNamespaces.join(", ");
        }
        return t("noNamespaces");
      } catch {
        enqueueSnackbar("Invalid namespaces format.", { variant: "error" });
        return "Invalid namespaces format.";
      }
    }

    return "Invalid namespaces data.";
  };

  const sectionTitleSx = {
    color: workspace.primary_dark,
    fontWeight: 600,
    fontSize: "0.9rem",
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
    pb: 0.5,
    mb: 1,
  };

  const sectionContentSx = {
    fontSize: "0.85rem",
    fontWeight: 500,
    color: colors.primary_dark,
    paddingLeft: 2,
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: colors.gray50 }}>
      <Box
        sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 0.5 }}
      >
        <Container maxWidth="lg" sx={{ marginTop: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              border: `1px solid ${colors.gray200}`,
              borderLeft: `4px solid ${colors.primary_dark}`,
              background: colors.white,
              color: colors.primary_dark,
            }}
          >
            <Grid container spacing={4} sx={{ position: "relative" }}>
              <Grid item xs={12} md={4}>
                {icon_image ? (
                  <Avatar
                    src={`data:image/png;base64,${icon_image}`}
                    alt={name || "Workspace Icon"}
                    sx={{
                      width: 150,
                      height: 150,
                      margin: "0 auto",
                      border: "2px solid white",
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 150,
                      height: 150,
                      margin: "0 auto",
                      backgroundColor: colors.primary_dark,
                      color: colors.white,
                      fontSize: 48,
                    }}
                  >
                    {name?.charAt(0).toUpperCase() || "N"}
                  </Avatar>
                )}
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid
                  container
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="h4" sx={{ color: colors.primary_dark }}>
                    {name || t("noName")}
                  </Typography>
                  <Box sx={{ display: "flex", marginLeft: 2 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        backgroundColor:
                          workspaceData?.primary_color || colors.primary_dark,
                        marginRight: 1,
                        mt: -1,
                        border: `1px solid ${colors.white}`,
                      }}
                      title={workspaceData?.primary_color || t("primaryColor")}
                    />
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        backgroundColor:
                          workspaceData?.secondary_color || colors.secondary,
                        marginRight: 1,
                        mt: -1,
                        border: `1px solid ${colors.white}`,
                      }}
                      title={
                        workspaceData?.secondary_color || t("secondaryColor")
                      }
                    />
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        backgroundColor:
                          workspaceData?.accent_color || colors.accent,
                        mt: -1,
                        border: `1px solid ${colors.white}`,
                      }}
                      title={workspaceData?.accent_color || t("accentColor")}
                    />
                  </Box>
                </Grid>
                <Typography
                  variant="subtitle1"
                  sx={{ fontStyle: "italic", marginBottom: 2 }}
                >
                  {description || t("noDescription")}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                  {t("createdAt")}: {formatDate(created_at)}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                }}
              >
                <Edit
                  onClick={handleEdit}
                  sx={{
                    cursor: "pointer",
                    color: colors.primary_dark,
                    fontSize: 32,
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ marginY: 4 }} />

            <Box sx={{ marginBottom: 4 }}>
              <Typography sx={sectionTitleSx}> {t("namespace")}</Typography>
              <Typography sx={sectionContentSx}>
                {renderNamespaces()}
              </Typography>
            </Box>

            <Divider sx={{ marginY: 4 }} />

            <Box
              sx={{
                textAlign: "center",
                marginBottom: 4,
              }}
            >
              <Typography sx={{ ...sectionTitleSx, fontWeight: "bold" }}>
                {t("limits")}
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      color: colors.primary_dark,
                    }}
                  >
                    {t("userEvaluationLimit")}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: "1.2rem",
                      fontWeight: 700,
                      color: colors.primary_dark,
                    }}
                  >
                    {user_evaluation_limit !== null
                      ? user_evaluation_limit
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      color: colors.primary_dark,
                    }}
                  >
                    {t("userEntityLimit")}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      mt: 1,
                      fontSize: "1.2rem",
                      fontWeight: 700,
                      color: colors.primary_dark,
                    }}
                  >
                    {user_company_limit !== null ? user_company_limit : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      color: colors.primary_dark,
                    }}
                  >
                    {t("userStorageLimit")}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      mt: 1,
                      fontSize: "1.2rem",
                      fontWeight: 700,
                      color: colors.primary_dark,
                    }}
                  >
                    {user_storage_limit !== null ? user_storage_limit : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      color: colors.primary_dark,
                    }}
                  >
                    {t("userLibraryLimit")}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      mt: 1,
                      fontSize: "1.2rem",
                      fontWeight: 700,
                      color: colors.primary_dark,
                    }}
                  >
                    {user_library_limit !== null ? user_library_limit : "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ marginY: 4 }} />

            <Box sx={{ marginBottom: 4 }}>
              <Typography sx={sectionTitleSx}>{t("workspaceAdmin")}</Typography>
              {workspace_admin ? (
                <Typography sx={sectionContentSx}>
                  {workspace_admin.name} {workspace_admin.surname} (
                  {workspace_admin.email})
                </Typography>
              ) : (
                <Typography sx={sectionContentSx}>{t("noAdmin")}</Typography>
              )}
            </Box>

            <Divider sx={{ marginY: 4 }} />

            <Box sx={{ marginBottom: 4 }}>
              <Typography sx={sectionTitleSx}>{t("models")}</Typography>
              {accessible_model_ids.length > 0 ? (
                accessible_model_ids.map((mid) => {
                  const model = modelData?.all_models?.find(
                    (m) => m.model_id === mid,
                  );
                  return (
                    <Typography sx={sectionContentSx}>
                      {model?.model_name ?? `Model ${mid}`}
                    </Typography>
                  );
                })
              ) : (
                <Typography sx={sectionContentSx}>{t("noModels")}</Typography>
              )}
            </Box>

            <Divider sx={{ marginY: 4 }} />

            <Box sx={{ marginBottom: 4 }}>
              <Typography sx={sectionTitleSx}>{t("terminology")}</Typography>

              <Typography sx={sectionContentSx}>
                {terminologyToString(workspaceData.terminology)}
              </Typography>
            </Box>

            <Divider sx={{ marginY: 4 }} />

            <Box sx={{ marginBottom: 4 }}>
              <Typography sx={sectionTitleSx}>{t("features")}</Typography>
              <Grid container spacing={2}>
                {permissions.length > 0 ? (
                  permissions.map((permission) => (
                    <Grid
                      item
                      key={permission.permission_id}
                      xs={12}
                      sm={6}
                      md={4}
                    >
                      <Card
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          transition: "background-color 0.3s ease",
                          "&:hover": {
                            borderColor: colors.gray300,
                          },
                          border: `1px solid ${colors.gray200}`,
                          backgroundColor: colors.white,
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography sx={sectionTitleSx}>
                            {permission.feature.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              marginTop: 1,
                              color: colors.primary_dark,
                              fontSize: "0.85rem",
                            }}
                          >
                            {permission.feature.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Card sx={{ padding: 2, backgroundColor: colors.white }}>
                      <Typography
                        sx={{
                          ...sectionContentSx,
                          textAlign: "center",
                        }}
                      >
                        {t("noPermissions")}
                      </Typography>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Divider sx={{ marginY: 4 }} />

            <Box>
              <Typography sx={sectionTitleSx}>{t("certifications")}</Typography>
              {workspace_certifications.length > 0 ? (
                workspace_certifications.map((certification) => (
                  <Typography sx={sectionContentSx}>
                    {certification.certification_category_name}
                  </Typography>
                ))
              ) : (
                <Typography sx={sectionContentSx}>
                  {t("noCertifications")}
                </Typography>
              )}
            </Box>
            <Divider sx={{ marginY: 4 }} />

            <Box sx={{ marginBottom: 4 }}>
              <Typography sx={sectionTitleSx}>
                {t("predefinedSections")}
              </Typography>
              {predefinedSectionsData?.appendix_structures &&
              predefinedSectionsData.appendix_structures.length > 0 ? (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {predefinedSectionsData.appendix_structures.map(
                    (structure) => {
                      const sectionCount = structure.structure?.length ?? 0;
                      const subsectionCount =
                        structure.structure?.reduce(
                          (acc, s) => acc + (s.subsections?.length ?? 0),
                          0,
                        ) ?? 0;
                      const title =
                        structure.structure?.[0]?.name ||
                        `Structure #${structure.id}`;

                      return (
                        <Grid item xs={12} sm={6} md={4} key={structure.id}>
                          <Card
                            onClick={() => {
                              setPreviewStructure(structure);
                              setPreviewOpen(true);
                            }}
                            sx={{
                              cursor: "pointer",
                              height: "100%",
                              transition:
                                "transform 0.2s ease, box-shadow 0.2s ease",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: `0 4px 12px rgba(3, 46, 77, 0.15)`,
                                borderColor: colors.accent,
                              },
                              border: `1px solid ${colors.gray200}`,
                              borderRadius: 2,
                            }}
                          >
                            <CardContent>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1,
                                    backgroundColor: colors.accent,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: colors.white,
                                      fontWeight: 700,
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    {sectionCount}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 600,
                                    color: colors.primary_dark,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {title}
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{ color: colors.gray500 }}
                              >
                                {sectionCount}{" "}
                                {sectionCount === 1 ? "section" : "sections"}
                                {subsectionCount > 0 &&
                                  ` · ${subsectionCount} ${subsectionCount === 1 ? "subsection" : "subsections"}`}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    },
                  )}
                </Grid>
              ) : (
                <Typography sx={sectionContentSx}>
                  {t("noPredefinedSections")}
                </Typography>
              )}
            </Box>
            <Divider sx={{ marginY: 4 }} />

            <Box sx={{ marginBottom: 4 }}>
              <Typography sx={sectionTitleSx}>
                {t("alanCredentials")}
              </Typography>

              <Typography sx={sectionContentSx}>
                {workspaceData?.alan_credentials || t("noAlanCredential")}
              </Typography>
            </Box>
            <Divider sx={{ marginY: 4 }} />

            <Box>
              <Typography sx={sectionTitleSx}> {t("chatExperts")}</Typography>
              {experts.length > 0 ? (
                experts.map((expert) => (
                  <Typography
                    key={expert.id}
                    variant="body1"
                    sx={{ paddingLeft: 2, marginBottom: 1 }}
                  >
                    {expert.title}
                  </Typography>
                ))
              ) : (
                <Typography sx={sectionContentSx}>{t("noExperts")}</Typography>
              )}
            </Box>
            <Divider sx={{ marginY: 4 }} />

            <Box sx={{ marginBottom: 4 }}>
              <Typography sx={sectionTitleSx}>{t("workspaceUsers")}</Typography>
              <UsersTable workspaceId={parseInt(workspaceId!, 10)} />
            </Box>
            <Divider sx={{ marginY: 4 }} />

            <Box sx={{ marginBottom: 4 }}>
              <Typography sx={sectionTitleSx}>
                {t("sharedQuestionLists")}
              </Typography>
              <WorkspaceQuestionListsTable
                data={workspaceData?.workspace_question_lists || []}
              />
            </Box>
            <Divider sx={{ marginY: 4 }} />

            <Box sx={{ marginBottom: 4 }}>
              <Typography sx={sectionTitleSx}>
                {t("sharedEntities", { entity: terminology.plural })}
              </Typography>
              <SharedEntitiesTable companies={workspaceData?.companies || []} />
            </Box>
            <Divider sx={{ marginY: 4 }} />

            <Box>
              <UsageWidget workspaceId={parseInt(workspaceId!, 10)} />
            </Box>
          </Paper>

          <PredefinedSectionPreviewModal
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            structure={previewStructure}
          />
        </Container>
      </Box>
    </Box>
  );
};

export default SingleWorkspace;
